import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Spinner, EmptyState } from "@/components/common";

const EMPTY = { name: "", email: "", title: "", organization: "" };

export function EvaluatorDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(initial ? { ...EMPTY, ...initial } : EMPTY); }, [initial, open]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, title: form.title || null, organization: form.organization || null };
      const r = initial?.id ? await api.put(`/admin/evaluators/${initial.id}`, payload) : await api.post("/admin/evaluators", payload);
      onSaved(r.data);
      onOpenChange(false);
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="evaluator-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Değerlendiriciyi Düzenle" : "Yeni Değerlendirici"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="ev-name">Ad Soyad</Label><Input id="ev-name" data-testid="evaluator-name-input" value={form.name} onChange={set("name")} required /></div>
          <div className="space-y-1.5"><Label htmlFor="ev-email">E-posta</Label><Input id="ev-email" data-testid="evaluator-email-input" type="email" value={form.email} onChange={set("email")} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label htmlFor="ev-title">Rol / Unvan</Label><Input id="ev-title" data-testid="evaluator-title-input" value={form.title || ""} onChange={set("title")} /></div>
            <div className="space-y-1.5"><Label htmlFor="ev-org">Kurum</Label><Input id="ev-org" data-testid="evaluator-org-input" value={form.organization || ""} onChange={set("organization")} /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>İptal</Button>
            <Button type="submit" data-testid="evaluator-save-button" disabled={busy} className="btn-press">Kaydet</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function EvaluatorsPage() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/evaluators").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (ev) => {
    if (!window.confirm(`${ev.name} silinsin mi? Bu değerlendiriciye ait tüm atamalar ve verdiği puanlar da silinecek.`)) return;
    try { await api.delete(`/admin/evaluators/${ev.id}`); load(); } catch (e) { toast.error(errText(e)); }
  };
  if (!items) return <Spinner />;
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Değerlendiriciler" title="Değerlendiriciler" description="Değerlendirmelere davet edilebilecek alan uzmanları." actions={<Button data-testid="evaluators-add-button" onClick={() => { setEditing(null); setOpen(true); }} className="btn-press rounded-full"><Plus className="mr-1.5 h-4 w-4" /> Değerlendirici Ekle</Button>} />
      {items.length === 0 ? <EmptyState testId="evaluators-empty" title="Henüz değerlendirici yok" description="Alan uzmanlarını ekleyin; ardından değerlendirmelere atayabilirsiniz." /> : (
        <div className="plena-card divide-y divide-plena-border">
          {items.map((ev) => (
            <div key={ev.id} data-testid={`evaluator-row-${ev.id}`} className="flex items-center justify-between gap-4 px-6 py-4">
              <div className="min-w-0">
                <p className="font-medium">{ev.name}</p>
                <p className="truncate text-sm text-plena-text2">{ev.email}{ev.title ? ` · ${ev.title}` : ""}{ev.organization ? ` · ${ev.organization}` : ""}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-plena-text2">{ev.submitted_count} / {ev.assignment_count} tamamlandı</span>
                <Button variant="ghost" size="icon" data-testid={`evaluator-edit-${ev.id}`} onClick={() => { setEditing(ev); setOpen(true); }} aria-label="Düzenle"><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" data-testid={`evaluator-delete-${ev.id}`} onClick={() => remove(ev)} aria-label="Sil" className="text-plena-text2 hover:text-plena-coral"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <EvaluatorDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
}
